import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Card,
  Icon,
  Dialog,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Box,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useUserData from "layouts/tables/data/userTableData";

function User() {
  const {
    columns,
    rows,
    isLoading,
    error,
    members,
    isLoadingMembers,
    handleMemberSelect,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedUser,
    setSelectedUser,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddUser,
  } = useUserData();

  // Safe access to members data
  const safeMembers = members || [];

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading users...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      User Accounts
                    </MDTypography>
                    <MDTypography variant="caption" color="white">
                      Manage church member accounts
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDButton variant="gradient" color="dark" onClick={handleAddUser}>
                      <Icon>add</Icon>&nbsp;New User
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search users..."
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <MDBox p={4}>
          <MDTypography variant="h5" gutterBottom>
            {selectedUser?.id ? "Edit User" : "Create New User"}
          </MDTypography>

          <Grid container spacing={3}>
            {/* Member Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="member-select-label">Select Member *</InputLabel>
                <Select
                  labelId="member-select-label"
                  value={selectedUser?.member_id || ""}
                  onChange={(e) => {
                    const selectedMemberId = e.target.value;
                    const selectedMember = safeMembers.find(
                      (member) => member.id === selectedMemberId
                    );
                    if (selectedMember) {
                      handleMemberSelect(selectedMember);
                    }
                  }}
                  input={<OutlinedInput label="Select Member *" />}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {isLoadingMembers ? (
                    <MenuItem disabled>
                      <Box display="flex" justifyContent="center" width="100%">
                        <CircularProgress size={20} />
                      </Box>
                    </MenuItem>
                  ) : safeMembers.length > 0 ? (
                    safeMembers.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.first_name} {member.surname}
                        {member.email && ` - ${member.email}`}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No members found</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Display selected member info */}
            {selectedUser?.member_id && (
              <Grid item xs={12}>
                <MDBox p={2} borderRadius="borderRadius" bgcolor="grey.100">
                  <MDTypography variant="button" fontWeight="medium">
                    Selected Member: {selectedUser.member_name}
                  </MDTypography>
                  {selectedUser.email && (
                    <MDTypography variant="caption" display="block">
                      Email: {selectedUser.email}
                    </MDTypography>
                  )}
                  {selectedUser.phone && (
                    <MDTypography variant="caption" display="block">
                      Phone: {selectedUser.phone}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
            )}

            {/* Username */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Username *"
                value={selectedUser?.username || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    username: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Email *"
                type="email"
                value={selectedUser?.email || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    email: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Phone"
                value={selectedUser?.phone || ""}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    phone: e.target.value,
                  })
                }
                fullWidth
              />
            </Grid>

            {/* Role */}
            <Grid item xs={12} md={6}>
              <MDInput
                select
                label="Role *"
                value={selectedUser?.role || "member"}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    role: e.target.value,
                  })
                }
                fullWidth
              >
                {["admin", "pastor", "staff", "member", "treasurer"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <MDInput
                select
                label="Status"
                value={selectedUser?.status || "active"}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    status: e.target.value,
                  })
                }
                fullWidth
              >
                {["active", "inactive"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>

            {/* Password fields for new users */}
            {!selectedUser?.id && (
              <>
                <Grid item xs={12} md={6}>
                  <MDInput
                    label="Password *"
                    type="password"
                    value={selectedUser?.password || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        password: e.target.value,
                      })
                    }
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    label="Confirm Password *"
                    type="password"
                    value={selectedUser?.confirmPassword || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        confirmPassword: e.target.value,
                      })
                    }
                    fullWidth
                    required
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* Action Buttons */}
          <MDBox display="flex" justifyContent="flex-end" mt={4}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setEditModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={handleEditSubmit}>
              {selectedUser?.id ? "Save Changes" : "Create User"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      {/* Delete User Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <MDBox p={3}>
          <MDTypography variant="h6" gutterBottom>
            Confirm Delete
          </MDTypography>
          <MDTypography>
            Are you sure you want to delete user `{selectedUser?.username}`?
          </MDTypography>
          <MDBox display="flex" justifyContent="flex-end" mt={3}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setDeleteModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="error" onClick={handleDeleteConfirm}>
              Confirm Delete
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

User.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedUser: PropTypes.object,
  setSelectedUser: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddUser: PropTypes.func,
};

export default User;
